import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, ArrowRight, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error(t('auth.reset_no_token'));
    }
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error(t('auth.reset_no_token'));
      return;
    }
    if (password !== confirm) {
      toast.error('passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('min 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success(t('auth.reset_success'));
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t('auth.reset_invalid'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="reset-page">
      <Navbar />
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-sm animate-fade-in-up">
          <div className="text-[10px] text-primary tracking-wider mb-4">$ auth --reset</div>
          <h1 className="text-xl font-bold tracking-tight mb-1" data-testid="reset-title">{t('auth.reset_title')}</h1>
          <p className="text-[11px] text-muted-foreground mb-8">{t('auth.reset_subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] text-muted-foreground tracking-wider">{t('auth.reset_new_password')}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="********" required dir="ltr" data-testid="reset-password-input"
                className="h-10 rounded bg-card border-border/50 text-xs" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] text-muted-foreground tracking-wider">{t('auth.reset_confirm')}</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="********" required dir="ltr" data-testid="reset-confirm-input"
                className="h-10 rounded bg-card border-border/50 text-xs" />
            </div>
            <Button type="submit" disabled={loading || !token} data-testid="reset-submit-btn"
              className="w-full h-10 rounded text-[11px] tracking-wider">
              {loading
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <>{t('auth.reset_submit')} <ArrowRight className="ms-1.5 h-3 w-3" /></>}
            </Button>
          </form>

          <Link to="/login" className="flex items-center justify-center gap-1.5 mt-6 text-[11px] text-muted-foreground hover:text-primary transition-colors" data-testid="back-to-login-link">
            <ArrowLeft className="h-3 w-3" />
            {t('auth.back_to_login')}
          </Link>
        </div>
      </div>
    </div>
  );
}
